<template>
    <li class="flex flex-col gap-2">
        <div class="flex justify-between">
            <label class="flex items-start gap-2 m-0">
                <input type="checkbox" class="custom-checkbox rounded border-gray-300 text-blue-600 mt-[2px]" 
                    :class="category.isIndeterminate ? 'indeterminate border-[#000]' : ''"
                    :checked="category.isSelected"
                    @change="onChange" />
                <span>{{ category.CategoryName }}</span>
                <span class="text-[#6E6E6E]" v-if="formattedCount">({{ formattedCount }})</span>
            </label>
            <div :id="`global-catalog-category-toggle-${String(category.CategoryName ?? category.CategoryId).split(' ').join('-')}`" 
                class="flex items-start cursor-pointer h-4" 
                v-if="hasChildCategory" 
                @click="toggleExpand">
                    <i class="ri-arrow-up-s-line text-xl text-[#464646] cursor-pointer h-0 -mt-1" v-if="category.isExpanded"></i>
                    <i class="ri-arrow-down-s-line text-xl text-[#464646] cursor-pointer h-0 -mt-1" v-else></i>
            </div>
        </div>

        <ul class="flex flex-col gap-2 pl-4" v-if="hasChildCategory" v-show="category.isExpanded">
            <CategoryFilterItem 
                v-for="cat in category.Categories"
                :category="cat"
                @remove-selected="removeSelected"
                @add-selected="addSelected" />
        </ul>
    </li>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useGlobalCatalogStore } from "@/stores/globalCatalogStore"
import CategoryFilterItem from '@/components/GlobalCatalog/CategoryFilterItem.vue';
import { formatFacetCount } from '@/utilities/facetCountFormat';

const globalCatalogStore = useGlobalCatalogStore();

const props = defineProps({
    category: {
        type: Object,
        default: {}
    }
});

const toggleExpand = () => {
    props.category.isExpanded = !props.category.isExpanded;
}

const emits = defineEmits(['add-selected', 'remove-selected'])

const hasChildCategory = computed(() => props.category.Categories.length > 0);

// Number of products matching this category in the current search, provided by the API facets.
// A count of 0 is still displayed and the option remains selectable.
const productCount = computed(() => globalCatalogStore.getCategoryProductCount(props.category));
const formattedCount = computed(() => formatFacetCount(productCount.value));

const isSelected = ref(false)
const onChange = event => {
    isSelected.value = event.target.checked;
    props.category.isExpanded = event.target.checked;
    updateSelectedCategory(props.category);
}

function updateSelectedCategory(category) {
    if ( category.isSelected !== isSelected.value ) {
        globalCatalogStore.setIsSelectedForCategories(category, isSelected.value)
        isSelected.value ? addSelected() : removeSelected();
    }
}

// The `removeSelected` function is responsible for deselecting the current category and emitting a
// 'remove-selected' event if the category has a parent category.
function removeSelected(indeterminate = false) {
    // works for emitted parent
    const isAnyChildSelected = props.category.Categories.find(cat => cat.isSelected === true);
    if ( isAnyChildSelected) {
        // Only some children will be selected
        // Since the emit is coming from a child which has been removed
        indeterminate = true;
    }
    props.category.isIndeterminate = indeterminate;

    props.category.isSelected = false
    if ( props.category.ParentCategoryId ) {
        emits('remove-selected', indeterminate)
    }
}

// The `addSelected` function is responsible for selecting the current category and emitting an
// 'add-selected' event if all child categories are also selected.
function addSelected(indeterminate = false) {
    props.category.isExpanded = true;
    if ( indeterminate ) {
        emits('add-selected', indeterminate)
        props.category.isIndeterminate = true;

    } else {
        const nonSelectedChild = props.category.Categories.find(cat => cat.isSelected !== true);
        if ( !nonSelectedChild ) {
            // All the children has been selected
            props.category.isSelected = true
            props.category.isIndeterminate = false;
            emits('add-selected')
        } else {
            emits('add-selected', true)
            props.category.isIndeterminate = true;
        }
    }
}

onMounted(() => {
    // To populate products based on the url
    const urlParams = new URLSearchParams(window.location.search);
    const selectedCategoryId = urlParams.get('categoryId'); 
    if ( selectedCategoryId === props.category.CategoryId.toString()) {
        globalCatalogStore.setIsSelectedForCategories(props.category, true)
        addSelected();
    }
})
</script>